
import { FSharpRef, Union } from "../fable_modules/fable-library-js.5.3.0/Types.js";
import { string_type, int32_type, union_type, decimal_type } from "../fable_modules/fable-library-js.5.3.0/Reflection.js";
import { fromParts, compare } from "../fable_modules/fable-library-js.5.3.0/Decimal.js";
import { FSharpResult$2 } from "../fable_modules/fable-library-js.5.3.0/Result.js";
import { create, match } from "../fable_modules/fable-library-js.5.3.0/RegExp.js";
import { isNullOrWhiteSpace, split, printf, toText, padRight, concat } from "../fable_modules/fable-library-js.5.3.0/String.js";
import { tryParse } from "../fable_modules/fable-library-js.5.3.0/Int32.js";
import { item, equalsWith } from "../fable_modules/fable-library-js.5.3.0/Array.js";
import { defaultOf } from "../fable_modules/fable-library-js.5.3.0/Util.js";

/**
 * Positive amount validation
 */
export class PositiveAmount extends Union {
    constructor(Item) {
        super();
        this.tag = 0;
        this.fields = [Item];
    }
    cases() {
        return ["PositiveAmount"];
    }
}

export function PositiveAmount_$reflection() {
    return union_type("Shared.PositiveAmount", [], PositiveAmount, () => [[["Item", decimal_type]]]);
}

export function PositiveAmountModule_create(amount) {
    if (compare(amount, fromParts(0, 0, 0, false, 0)) > 0) {
        return new FSharpResult$2(0, [new PositiveAmount(amount)]);
    }
    else {
        return new FSharpResult$2(1, ["Amount must be positive"]);
    }
}

export function PositiveAmountModule_value(_arg) {
    return _arg.fields[0];
}

/**
 * Money amount in whole cents, zero or greater.
 * A challenge fee may legitimately be zero, so PositiveAmount is not usable here.
 */
export class FeeAmountCents extends Union {
    constructor(Item) {
        super();
        this.tag = 0;
        this.fields = [Item];
    }
    cases() {
        return ["FeeAmountCents"];
    }
}

export function FeeAmountCents_$reflection() {
    return union_type("Shared.FeeAmountCents", [], FeeAmountCents, () => [[["Item", int32_type]]]);
}

export function FeeAmountCentsModule_create(cents) {
    if (cents >= 0) {
        return new FSharpResult$2(0, [new FeeAmountCents(cents)]);
    }
    else {
        return new FSharpResult$2(1, ["Fee amount cannot be negative"]);
    }
}

export function FeeAmountCentsModule_value(_arg) {
    return _arg.fields[0] | 0;
}

/**
 * An amount expressed as a decimal-currency-unit string (e.g. "25.00"), converted to whole
 * cents. Rejects anything other than plain digits with an optional single-dot fraction of
 * 1-2 digits — no sign, thousands separators, or exponent notation. Used where an external
 * payload reports an amount as a decimal string in currency units rather than as integer
 * cents (e.g. Every.org's Partner Webhook, https://docs.every.org/docs/webhooks/partner-webhook).
 * Parsed with a manual regex + Int32 split rather than System.Decimal.TryParse's
 * NumberStyles/CultureInfo overload, which Fable does not support.
 */
export class DecimalCurrencyAmountCents extends Union {
    constructor(Item) {
        super();
        this.tag = 0;
        this.fields = [Item];
    }
    cases() {
        return ["DecimalCurrencyAmountCents"];
    }
}

export function DecimalCurrencyAmountCents_$reflection() {
    return union_type("Shared.DecimalCurrencyAmountCents", [], DecimalCurrencyAmountCents, () => [[["Item", int32_type]]]);
}

const DecimalCurrencyAmountCentsModule_pattern = "^([0-9]+)(?:\\.([0-9]{1,2}))?$";

export function DecimalCurrencyAmountCentsModule_create(amountText) {
    const m = match(create(DecimalCurrencyAmountCentsModule_pattern), amountText);
    if (!(m != null)) {
        return new FSharpResult$2(1, [concat("is not a decimal amount with at most two decimal places: ", amountText)]);
    }
    else {
        const wholeText = m[1] || "";
        const fracText = (m[2] != null) ? padRight(m[2] || "", 2, "0") : "00";
        let matchValue;
        let outArg = 0;
        matchValue = [tryParse(wholeText, 511, false, 32, new FSharpRef(() => (outArg | 0), (v) => {
            outArg = (v | 0);
        })), outArg];
        let matchValue_1;
        let outArg_1 = 0;
        matchValue_1 = [tryParse(fracText, 511, false, 32, new FSharpRef(() => (outArg_1 | 0), (v_1) => {
            outArg_1 = (v_1 | 0);
        })), outArg_1];
        let matchResult;
        if (matchValue[0]) {
            if (matchValue_1[0]) {
                if (matchValue[1] <= ~~((2147483647 - matchValue_1[1]) / 100)) {
                    matchResult = 0;
                }
                else {
                    matchResult = 1;
                }
            }
            else {
                matchResult = 1;
            }
        }
        else {
            matchResult = 1;
        }
        switch (matchResult) {
            case 0:
                return new FSharpResult$2(0, [new DecimalCurrencyAmountCents((matchValue[1] * 100) + matchValue_1[1])]);
            default:
                return new FSharpResult$2(1, [concat("is too large: ", amountText)]);
        }
    }
}

export function DecimalCurrencyAmountCentsModule_value(_arg) {
    return _arg.fields[0] | 0;
}

/**
 * A currency code checked against the single currency a caller accepts (e.g. Every.org
 * donation webhooks, which our donate links only ever request in USD).
 */
export class RequiredCurrency extends Union {
    constructor(Item) {
        super();
        this.tag = 0;
        this.fields = [Item];
    }
    cases() {
        return ["RequiredCurrency"];
    }
}

export function RequiredCurrency_$reflection() {
    return union_type("Shared.RequiredCurrency", [], RequiredCurrency, () => [[["Item", string_type]]]);
}

export function RequiredCurrencyModule_create(expected, actual) {
    if (actual === expected) {
        return new FSharpResult$2(0, [new RequiredCurrency(actual)]);
    }
    else {
        return new FSharpResult$2(1, [`Unsupported currency ${actual} (expected ${expected})`]);
    }
}

export function RequiredCurrencyModule_value(_arg) {
    return _arg.fields[0];
}

/**
 * Validated time-of-day value restricted to 15-minute increments (hour 0-23, minute 0/15/30/45).
 * Used for user preferences and challenge scheduling (start time, reminder time).
 */
export class TimeOfDay extends Union {
    constructor(hour, minute) {
        super();
        this.tag = 0;
        this.fields = [hour, minute];
    }
    cases() {
        return ["TimeOfDay"];
    }
}

export function TimeOfDay_$reflection() {
    return union_type("Shared.TimeOfDay", [], TimeOfDay, () => [[["hour", int32_type], ["minute", int32_type]]]);
}

export function TimeOfDayModule_create(hour, minute) {
    if ((hour < 0) ? true : (hour > 23)) {
        return new FSharpResult$2(1, ["Hour must be between 0 and 23"]);
    }
    else if ((minute < 0) ? true : (minute > 59)) {
        return new FSharpResult$2(1, ["Minute must be between 0 and 59"]);
    }
    else if ((minute % 15) !== 0) {
        return new FSharpResult$2(1, ["Minute must be a 15-minute increment (0, 15, 30, or 45)"]);
    }
    else {
        return new FSharpResult$2(0, [new TimeOfDay(hour, minute)]);
    }
}

export function TimeOfDayModule_hour(_arg) {
    return _arg.fields[0] | 0;
}

export function TimeOfDayModule_minute(_arg) {
    return _arg.fields[1] | 0;
}

export const TimeOfDayModule_defaultValue = new TimeOfDay(9, 0);

/**
 * Convert to total minutes since midnight
 */
export function TimeOfDayModule_toTotalMinutes(_arg) {
    return ((_arg.fields[0] * 60) + _arg.fields[1]) | 0;
}

/**
 * Create from total minutes, wrapping around 24h and truncating to the previous 15-minute boundary
 */
export function TimeOfDayModule_fromTotalMinutes(totalMinutes) {
    const snapped = (~~((((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60)) / 15) * 15) | 0;
    return new TimeOfDay(~~(snapped / 60), snapped % 60);
}

/**
 * Format as HH:mm string (24-hour)
 */
export function TimeOfDayModule_toStringFormatted(_arg) {
    return toText(printf("%02d:%02d"))(_arg.fields[0])(_arg.fields[1]);
}

/**
 * Parse from HH:mm string
 */
export function TimeOfDayModule_parseFormatted(s) {
    const matchValue = split(s, [":"], undefined, 0);
    if (!equalsWith((x, y) => (x === y), matchValue, defaultOf()) && (matchValue.length === 2)) {
        const mStr = item(1, matchValue);
        let matchValue_1;
        let outArg = 0;
        matchValue_1 = [tryParse(item(0, matchValue), 511, false, 32, new FSharpRef(() => (outArg | 0), (v) => {
            outArg = (v | 0);
        })), outArg];
        let matchValue_2;
        let outArg_1 = 0;
        matchValue_2 = [tryParse(mStr, 511, false, 32, new FSharpRef(() => (outArg_1 | 0), (v_1) => {
            outArg_1 = (v_1 | 0);
        })), outArg_1];
        let matchResult;
        if (matchValue_1[0]) {
            if (matchValue_2[0]) {
                matchResult = 0;
            }
            else {
                matchResult = 1;
            }
        }
        else {
            matchResult = 1;
        }
        switch (matchResult) {
            case 0:
                return TimeOfDayModule_create(matchValue_1[1], matchValue_2[1]);
            default:
                return new FSharpResult$2(1, ["Invalid time format, expected HH:mm"]);
        }
    }
    else {
        return new FSharpResult$2(1, ["Invalid time format, expected HH:mm"]);
    }
}

/**
 * Non-empty string validation
 */
export class NonEmptyString extends Union {
    constructor(Item) {
        super();
        this.tag = 0;
        this.fields = [Item];
    }
    cases() {
        return ["NonEmptyString"];
    }
}

export function NonEmptyString_$reflection() {
    return union_type("Shared.NonEmptyString", [], NonEmptyString, () => [[["Item", string_type]]]);
}

export function NonEmptyStringModule_create(str) {
    if (isNullOrWhiteSpace(str)) {
        return new FSharpResult$2(1, ["String cannot be empty"]);
    }
    else {
        return new FSharpResult$2(0, [new NonEmptyString(str.trim())]);
    }
}

export function NonEmptyStringModule_value(_arg) {
    return _arg.fields[0];
}

//# sourceMappingURL=BaseTypes.js.map
